import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { ROLE_PERMISSIONS } from '../types/users';
import { authService } from '../services/api';
import { useCacheCleaner } from '../hooks/useCacheCleaner';
// TODO: Intégrer avec l'API des utilisateurs côté serveur
// import { usersDB, techniciansDB } from '../db/database';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const { clearAllCaches } = useCacheCleaner();
  const [userState, setUserState] = useState({
    user: null,
    changeId: Date.now()
  });
  
  const [loading, setLoading] = useState(true);

  // Getters pour rétro-compatibilité
  const currentUser = userState.user;
  const userChangeId = userState.changeId;

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        
        // Vérifier si un utilisateur est déjà connecté
        const storedUser = localStorage.getItem('currentUser');
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            console.log('👤 Utilisateur restauré depuis localStorage:', parsedUser);
            
            // Restaurer ou générer le userChangeId
            const changeId = parsedUser.userChangeId || Date.now();
            console.log('🔄 UserChangeId restauré:', changeId);
            
            // MISE À JOUR ATOMIQUE : User + ChangeId ensemble
            setUserState({
              user: parsedUser,
              changeId: changeId
            });
            
            // Mettre à jour le localStorage avec l'ID si nécessaire
            if (!parsedUser.userChangeId) {
              const updatedUser = { ...parsedUser, userChangeId: changeId };
              localStorage.setItem('currentUser', JSON.stringify(updatedUser));
            }
          } catch (error) {
            console.error('❌ Erreur parsing utilisateur stocké:', error);
            // Nettoyer les données corrompues
            localStorage.removeItem('currentUser');
          }
        }
        
      } catch (error) {
        console.error('Erreur lors de l\'initialisation:', error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const login = async (email, password) => {
    try {
      console.log('🔐 Tentative de connexion:', email);
      
      // Vérifier s'il y a un changement d'utilisateur
      const currentUserEmail = currentUser?.email;
      const isUserChange = currentUserEmail && currentUserEmail !== email;
      
      if (isUserChange) {
        console.log('🔄 Changement d\'utilisateur détecté:', currentUserEmail, '->', email);
        console.log('🧹 Nettoyage complet et rechargement...');
        
        // Nettoyer complètement l'état utilisateur d'abord
        setUserState({ user: null, changeId: Date.now() });
        
        // Nettoyer tous les caches et le localStorage
        await clearAllCaches();
        localStorage.removeItem('selectedTeamId');
        
        // Utiliser l'API serveur pour l'authentification
        const response = await authService.login(email, password);
        
        if (response.success && response.user) {
          console.log('✅ Connexion réussie - génération nouvel ID changement');
          
          // Sauvegarder le token pour les requêtes suivantes
          if (response.token) {
            localStorage.setItem('authToken', response.token);
          }

          // Générer un nouvel ID de changement pour forcer le rechargement
          const newChangeId = Date.now();
          
          // Stocker le nouvel utilisateur avec l'ID de changement
          const userWithChangeId = { ...response.user, userChangeId: newChangeId };
          
          console.log('🔄 Mise à jour atomique complète (changement)');
          
          // PROTECTION : Sauvegarder AVANT de mettre à jour l'état pour éviter les courses
          localStorage.setItem('currentUser', JSON.stringify(userWithChangeId));
          
          // Sauvegarder l'équipe principale comme équipe par défaut (après nettoyage)
          if (response.user.primaryTeamId) {
            localStorage.setItem('selectedTeamId', response.user.primaryTeamId.toString());
            console.log('💾 Équipe principale sauvegardée comme défaut:', response.user.primaryTeamId);
          }
          
          // Ensuite mettre à jour l'état React
          flushSync(() => {
            setUserState({
              user: userWithChangeId,
              changeId: newChangeId
            });
          });
          
          console.log('✅ Propagation synchrone terminée, navigation autorisée');
          
          console.log('➡️ Navigation vers le calendrier (changement)');
          navigate('/calendar', { replace: true });
          return userWithChangeId;
        } else {
          throw new Error('Réponse invalide du serveur');
        }
      } else {
        // Connexion normale (pas de changement d'utilisateur)
        console.log('🔑 Connexion normale pour:', email);
        const response = await authService.login(email, password);
        
        if (response.success && response.user) {
          console.log('✅ Connexion réussie:', response.user);
          
          // Sauvegarder le token pour les requêtes suivantes
          if (response.token) {
            localStorage.setItem('authToken', response.token);
          }

          // Générer un nouvel ID de changement pour être sûr
          const newChangeId = Date.now();
          console.log('🆕 Génération userChangeId pour connexion normale:', newChangeId);
          
          // Mettre à jour l'utilisateur courant avec l'ID de changement
          const userWithChangeId = { ...response.user, userChangeId: newChangeId };
          
          console.log('🔄 Mise à jour atomique complète');
          
          // PROTECTION : Sauvegarder AVANT de mettre à jour l'état pour éviter les courses
          localStorage.setItem('currentUser', JSON.stringify(userWithChangeId));
          
          // Nettoyer d'abord l'ancienne équipe, puis sauvegarder la nouvelle
          localStorage.removeItem('selectedTeamId');
          if (response.user.primaryTeamId) {
            localStorage.setItem('selectedTeamId', response.user.primaryTeamId.toString());
            console.log('💾 Équipe principale sauvegardée comme défaut:', response.user.primaryTeamId);
          }
          
          // Ensuite mettre à jour l'état React avec flushSync
          flushSync(() => {
            setUserState({
              user: userWithChangeId,
              changeId: newChangeId
            });
          });
          
          console.log('✅ Propagation synchrone terminée, navigation autorisée');
          
          console.log('➡️ Navigation vers le calendrier');
          navigate('/calendar', { replace: true });
          return userWithChangeId;
        } else {
          throw new Error('Réponse invalide du serveur');
        }
      }
    } catch (error) {
      console.error('❌ Erreur de connexion:', error);
      throw new Error(error.message || 'Erreur de connexion');
    }
  };

  const logout = async () => {
    console.log('🚪 Déconnexion en cours...');
    
    try {
      // Nettoyer complètement l'état utilisateur d'abord
      setUserState({ user: null, changeId: Date.now() });
      
      // Utiliser le hook de nettoyage complet
      await clearAllCaches();
      
      // Forcer un rechargement complet vers la page de login
      console.log('🔄 Redirection vers la page de connexion...');
      window.location.replace('/login');
      
    } catch (error) {
      console.error('❌ Erreur lors de la déconnexion:', error);
      // En cas d'erreur, forcer quand même la redirection
      window.location.replace('/login');
    }
  };

  // Fonction pour forcer un changement d'utilisateur (utile pour les tests)
  const switchUser = async (newUser) => {
    console.log('🔄 Changement forcé d\'utilisateur:', currentUser?.email, '->', newUser?.email);
    
    try {
      // Nettoyer d'abord l'état utilisateur
      setUserState({ user: null, changeId: Date.now() });
      
      // Nettoyer tous les caches
      await clearAllCaches();
      
      // Attendre un peu pour s'assurer que le nettoyage est complet
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Mettre à jour le nouvel utilisateur
      const newChangeId = Date.now();
      setUserState({
        user: newUser,
        changeId: newChangeId
      });
      localStorage.setItem('currentUser', JSON.stringify(newUser));
      
    } catch (error) {
      console.error('❌ Erreur lors du changement d\'utilisateur:', error);
      throw error;
    }
  };

  const hasPermission = (permission) => {
    if (!currentUser) return false;
    const userPermissions = ROLE_PERMISSIONS[currentUser.role] || [];
    return userPermissions.includes(permission);
  };

  const value = {
    currentUser,
    user: currentUser,
    userChangeId,
    login,
    logout,
    switchUser,
    hasPermission,
    loading
  };

  if (loading) {
    return null;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 